import React, { useState, useEffect } from "react";
import { X, CheckCircle2, FileText, Loader2, Lock, Sparkles, Zap, ShieldAlert, Wand2 } from "lucide-react";
import { listPackages, getCodeViolations } from "../lib/api";
import { toast } from "sonner";
import TosConsentCheckbox from "./TosConsentCheckbox";

export default function SignoffDialog({
  open,
  onClose,
  onConfirm,
  onCheckout,
  design,
  result,
  loading,
  paywall,
  credits,
  checkingOut,
  pollingPayment,
  userEmail,
  onReviewViolations,
  onAutoResolveAll,
  autoResolving = false,
}) {
  const [packages, setPackages] = useState(null);
  const [selectedPack, setSelectedPack] = useState("value"); // default to better-value pack
  // V2.0.20 — ToS audit-row id required before the Stripe checkout
  // button enables. Reset whenever the dialog closes so each new
  // checkout requires a fresh affirmative tick.
  const [tosAcceptanceId, setTosAcceptanceId] = useState(null);
  const tosAccepted = !!tosAcceptanceId;
  // V1.9.1 — reviewer email input. Optional; if filled, the backend
  // also CCs this address. Pure UX-controlled — `onConfirm(reviewerEmail)`
  // bubbles the value up to the Editor's signoff handler.
  const [reviewerEmail, setReviewerEmail] = useState("");
  const [reviewerError, setReviewerError] = useState("");
  // V1.9.6 — Code-compliance pre-flight summary. We fetch on dialog open
  // and surface a warning banner if there are critical / warning issues
  // so users don't spend a credit on a non-compliant package.
  const [violationSummary, setViolationSummary] = useState(null);

  // V2.0.20 — reset ToS acceptance whenever the dialog closes
  useEffect(() => {
    if (!open) setTosAcceptanceId(null);
  }, [open]);

  const validateEmail = (e) => {
    if (!e) return true; // empty is allowed (reviewer is optional)
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
  };

  const handleConfirm = () => {
    const trimmed = reviewerEmail.trim();
    if (trimmed && !validateEmail(trimmed)) {
      setReviewerError("Please enter a valid email address (or leave blank)");
      return;
    }
    setReviewerError("");
    onConfirm(trimmed || null);
  };

  useEffect(() => {
    if (!open || packages) return;
    listPackages()
      .then((r) => setPackages(r.data.packages))
      .catch(() => {
        setPackages([
          { id: "concept", label: "Blueprint 2D PDF only", price_usd: 19, credits: 1, per_credit_usd: 19 },
          { id: "value", label: "Value Pack (5× Blueprint 2D PDF)", price_usd: 79, credits: 5, per_credit_usd: 15.8 },
        ]);
      });
  }, [open, packages]);

  // V1.9.6 — fetch code-compliance summary whenever the dialog opens for a
  // not-yet-paid (showConfirm) flow. Idempotent + cheap (read-only).
  useEffect(() => {
    if (!open || !design?.design_id) return;
    let cancelled = false;
    setViolationSummary(null);
    getCodeViolations(design.design_id)
      .then((r) => { if (!cancelled) setViolationSummary(r.data?.summary || null); })
      .catch(() => { /* silent — the warning is advisory */ });
    return () => { cancelled = true; };
  }, [open, design?.design_id]);

  if (!open) return null;
  const showPaywall = !result && paywall;
  const showConfirm = !result && !paywall;
  const selected = (packages || []).find((p) => p.id === selectedPack) || null;

  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-950/80 backdrop-blur-md grid place-items-center p-4"
      data-testid="signoff-dialog"
    >
      <div className="w-full max-w-2xl bg-zinc-900 border border-zinc-800 fade-up">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400">
              // Workflow
            </div>
            <div className="font-display text-lg tracking-tight text-zinc-50 mt-0.5">
              {showPaywall ? "Choose Your Pack" : "Purchase My Package"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-50 transition-colors"
            data-testid="signoff-dialog-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {pollingPayment && !result && (
          <div className="px-5 py-8 text-center space-y-3" data-testid="signoff-polling">
            <Loader2 className="w-7 h-7 text-brass-400 mx-auto animate-spin" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400">
              // Verifying payment
            </div>
            <p className="font-body text-sm text-zinc-400">
              Confirming with Stripe and crediting your account...
            </p>
          </div>
        )}

        {showPaywall && !pollingPayment && (
          <div className="px-5 py-5 space-y-5" data-testid="signoff-paywall">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 grid place-items-center border border-orange-700/60 bg-orange-950/30">
                <Lock className="w-4 h-4 text-orange-400" />
              </div>
              <div>
                <div className="font-display text-base text-zinc-50">
                  Unlock Professional Sign-off
                </div>
                <p className="font-body text-sm text-zinc-400 mt-1.5">
                  Choose a pack. Credits don't expire and apply to any project.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(packages || []).map((p) => {
                const active = p.id === selectedPack;
                const isValue = p.id === "value";
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelectedPack(p.id)}
                    data-testid={`pack-card-${p.id}`}
                    className={
                      "relative text-left border p-4 transition-colors " +
                      (active
                        ? "border-brass-500 bg-brass-950/20"
                        : "border-zinc-800 hover:border-zinc-600 bg-zinc-950")
                    }
                  >
                    {isValue && (
                      <div className="absolute -top-2 right-3 font-mono text-[9px] uppercase tracking-widest bg-orange-600 text-white px-2 py-0.5">
                        Best value
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                        {isValue ? "Bundle" : "One-time"}
                      </div>
                      <div className="font-mono text-[9px] uppercase tracking-widest text-brass-400 flex items-center gap-1">
                        {isValue ? <Zap className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                        {p.credits} credit{p.credits === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div className="font-display text-zinc-50 mt-1.5 text-2xl tracking-tight">
                      {p.label}
                    </div>
                    <div className="flex items-end gap-2 mt-2">
                      <div className="font-display text-3xl text-zinc-50 tracking-tight">
                        ${Number(p.price_usd).toFixed(0)}
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                        ${Number(p.per_credit_usd).toFixed(2)}/credit
                      </div>
                    </div>
                    {isValue && (
                      <div className="mt-2 font-mono text-[10px] text-orange-400 uppercase tracking-widest">
                        Save ~17% vs single PDF
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <ul className="space-y-2 font-body text-sm text-zinc-300">
              <li className="flex items-start gap-2">
                <span className="text-brass-400 font-mono text-xs mt-0.5">→</span>
                Multi-page PDF (cover · BIM tables · raw JSON)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brass-400 font-mono text-xs mt-0.5">→</span>
                Live render snapshot embedded in cover page
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brass-400 font-mono text-xs mt-0.5">→</span>
                Delivered via Resend to you and the review team
              </li>
            </ul>

            {/* V2.0.20 — Required ToS acceptance gates the Stripe checkout */}
            <div className="pt-2 border-t border-zinc-800">
              <TosConsentCheckbox
                surface="stripe_checkout"
                designId={design?.design_id}
                testIdPrefix="signoff-tos-accept"
                onAccepted={(id) => setTosAcceptanceId(id)}
                onRevoked={() => setTosAcceptanceId(null)}
                onError={() =>
                  toast.error("Could not record your acceptance. Please retry.")
                }
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-800">
              <button
                onClick={onClose}
                className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-zinc-50 px-4 py-2 border border-zinc-800 transition-colors"
                data-testid="signoff-paywall-cancel"
              >
                Maybe later
              </button>
              <button
                onClick={() => onCheckout(selectedPack)}
                disabled={checkingOut || !selected || !tosAccepted}
                data-testid="signoff-checkout-btn"
                title={
                  !tosAccepted
                    ? "Please accept the Terms of Service above to continue to payment."
                    : undefined
                }
                className="font-mono text-[10px] uppercase tracking-widest bg-orange-600 text-white px-5 py-2 hover:bg-orange-500 transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {checkingOut ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirecting
                  </>
                ) : (
                  <>
                    Pay ${selected ? Number(selected.price_usd).toFixed(0) : "—"} & Continue
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {showConfirm && !pollingPayment && (
          <div className="px-5 py-5 space-y-4">
            <p className="text-sm text-zinc-300 leading-relaxed">
              We'll package <span className="text-zinc-50 font-medium">{design?.name}</span> into a
              multi-page <span className="text-brass-400">Blueprint 2D PDF</span> with a snapshot
              of the live 3D render, and email it to you. You can also add an architect or reviewer
              to CC.
            </p>
            <div className="grid grid-cols-3 border border-zinc-800 divide-x divide-zinc-800">
              <div className="p-3">
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">P. 1</div>
                <div className="font-mono text-xs text-zinc-200 mt-1">Cover + Render</div>
              </div>
              <div className="p-3">
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">P. 2</div>
                <div className="font-mono text-xs text-zinc-200 mt-1">BIM Metadata</div>
              </div>
              <div className="p-3">
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">P. 3</div>
                <div className="font-mono text-xs text-zinc-200 mt-1">Raw JSON</div>
              </div>
            </div>

            {/* V1.9.6 → V2.0.33 — Code-compliance pre-flight banner.
                Brass-toned to match the Architect's Studio palette and
                wires the Auto-Resolve All shortcut so users can ship a
                code-compliant package without leaving the dialog. The
                "Review" link still opens the full Code Compliance
                dialog for users who'd rather inspect the list. */}
            {violationSummary && (violationSummary.critical > 0 || violationSummary.warning > 0) && (() => {
              const total = (violationSummary.critical || 0) + (violationSummary.warning || 0);
              return (
                <div
                  data-testid="signoff-code-warning"
                  className="border border-brass-700/60 bg-brass-950/30 px-4 py-3 flex items-start gap-3"
                >
                  <ShieldAlert className="w-4 h-4 text-brass-300 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-brass-200 mb-1">
                      // CODE COMPLIANCE — {violationSummary.critical} critical · {violationSummary.warning} warning
                    </div>
                    <div className="text-xs text-zinc-300 leading-relaxed">
                      Your design has unresolved building-code findings. Ship a
                      compliant package by auto-resolving them now, or review
                      and let the architect sign off as-is.
                    </div>
                    <div className="flex items-center gap-2 mt-2.5">
                      {onAutoResolveAll && (
                        <button
                          type="button"
                          onClick={onAutoResolveAll}
                          disabled={autoResolving}
                          data-testid="signoff-auto-resolve-btn"
                          className="font-mono text-[10px] uppercase tracking-widest border border-brass-500/60 bg-brass-900/40 text-brass-100 hover:bg-brass-800/40 px-3 py-1.5 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {autoResolving ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Wand2 className="w-3 h-3" />
                          )}
                          {autoResolving ? "Resolving…" : `Auto-Resolve (${total})`}
                        </button>
                      )}
                      {onReviewViolations && (
                        <button
                          type="button"
                          onClick={onReviewViolations}
                          data-testid="signoff-review-violations-btn"
                          className="font-mono text-[10px] uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:bg-zinc-800/60 px-3 py-1.5 transition-colors"
                        >
                          Review
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* V1.9.1 — Recipient breakdown. Primary recipient = signed-in
                user; the reviewer field is purely optional. */}
            <div className="space-y-3 border border-zinc-800 px-4 py-3 bg-zinc-950/40">
              <div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
                  Sent to
                </div>
                <div
                  data-testid="signoff-primary-recipient"
                  className="font-mono text-xs text-brass-300"
                >
                  {userEmail || "your signed-in account"}
                </div>
              </div>
              <div>
                <label
                  htmlFor="signoff-reviewer-email"
                  className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1 block"
                >
                  // CC architect / reviewer (optional)
                </label>
                <input
                  id="signoff-reviewer-email"
                  type="email"
                  inputMode="email"
                  placeholder="architect@example.com"
                  value={reviewerEmail}
                  onChange={(e) => {
                    setReviewerEmail(e.target.value);
                    if (reviewerError) setReviewerError("");
                  }}
                  data-testid="signoff-reviewer-email"
                  className="w-full bg-zinc-900 border border-zinc-700 focus:border-brass-500 focus:outline-none px-3 py-2 text-zinc-100 text-sm font-mono placeholder:text-zinc-600"
                />
                {reviewerError && (
                  <div
                    data-testid="signoff-reviewer-error"
                    className="font-mono text-[10px] text-orange-400 mt-1"
                  >
                    {reviewerError}
                  </div>
                )}
              </div>
            </div>

            {typeof credits === "number" && (
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-brass-400" />
                {credits} sign-off credit{credits === 1 ? "" : "s"} available
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={onClose}
                className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-zinc-50 px-4 py-2 border border-zinc-800 transition-colors"
                data-testid="signoff-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading}
                className="font-mono text-[10px] uppercase tracking-widest bg-orange-600 text-white px-5 py-2 hover:bg-orange-500 transition-colors flex items-center gap-2 disabled:opacity-60"
                data-testid="signoff-confirm-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Packaging
                  </>
                ) : (
                  <>Send to my email</>
                )}
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="px-5 py-6 space-y-4" data-testid="signoff-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-7 h-7 text-brass-400 mt-0.5" />
              <div>
                <div className="font-display text-lg text-zinc-50">Package sent</div>
                <div className="font-body text-sm text-zinc-400 mt-1">
                  We've packaged your design and emailed it to{" "}
                  <span className="text-brass-400 font-mono">{result.client_email}</span>
                  {result.reviewer_email && (
                    <>
                      {" "}with a copy to{" "}
                      <span
                        className="text-brass-400 font-mono"
                        data-testid="signoff-reviewer-confirmation"
                      >
                        {result.reviewer_email}
                      </span>
                    </>
                  )}
                  .
                </div>
              </div>
            </div>
            <div className="border border-zinc-800 p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-zinc-400" />
                <span className="font-mono text-xs text-zinc-200">{result.filename}</span>
              </div>
              <a
                href={`data:application/pdf;base64,${result.pdf_base64}`}
                download={result.filename}
                className="font-mono text-[10px] uppercase tracking-widest text-brass-400 hover:text-brass-300"
                data-testid="signoff-download-pdf"
              >
                Download
              </a>
            </div>
            {result.email_mocked && (
              <div className="border border-orange-700/50 bg-orange-950/20 p-3 font-mono text-[10px] uppercase tracking-widest text-orange-400">
                Email is currently MOCKED. Add a real `re_...` Resend API key to go live.
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="font-mono text-[10px] uppercase tracking-widest bg-zinc-100 text-zinc-900 px-5 py-2 hover:bg-white transition-colors"
                data-testid="signoff-done-btn"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
